import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreerBulletin } from '../hooks/useBulletins';
import { PeriodeSelector } from '../components/PeriodeSelector';
import { useEmployes, useEmploye } from '../../employes/hooks/useEmployes';

const schema = z.object({
  idEmploye: z.string().min(1, 'Sélectionnez un employé valide'),
  mois: z.number().int().min(1).max(12),
  annee: z.number().int().min(2020),
  avanceAcompte: z.number().min(0).default(0),
  oppositions: z.number().min(0).default(0),
  taxesTemporaires: z.number().min(0).default(0),
});

type FormData = z.infer<typeof schema>;

export function BulletinGenerationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idEmployeParam = searchParams.get('idEmploye');
  const creerBulletin = useCreerBulletin();
  const now = new Date();

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { mois: now.getMonth() + 1, annee: now.getFullYear(), avanceAcompte: 0, oppositions: 0, taxesTemporaires: 0 },
  });

  const mois = watch('mois');
  const annee = watch('annee');

  const [recherche, setRecherche] = useState('');
  const [idSelectionne, setIdSelectionne] = useState(idEmployeParam ?? '');

  const { data: employesData = [], isLoading: chargementEmployes } = useEmployes({ actif: true });
  const employes = Array.isArray(employesData) ? employesData : (employesData as any).items ?? [];
  const employesFiltres = employes
    .filter((e: any) => `${e.nom} ${e.prenoms} ${e.fonction}`.toLowerCase().includes(recherche.toLowerCase()))
    .slice(0, 20);

  // Charger l'employé pré-sélectionné directement par son ID
  const { data: employePreselectionne } = useEmploye(idEmployeParam ?? undefined);

  useEffect(() => {
    if (idEmployeParam && employePreselectionne) {
      setValue('idEmploye', idEmployeParam);
      setIdSelectionne(idEmployeParam);
      setRecherche(`${employePreselectionne.nom} ${employePreselectionne.prenoms}`);
    }
  }, [idEmployeParam, employePreselectionne?.idEmploye]);

  const onSubmit = async (data: FormData) => {
    const bulletin = await creerBulletin.mutateAsync(data);
    navigate(`/paie/bulletins/${bulletin.idBulletin}`);
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold text-content mb-6">Générer un bulletin de paie</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 bg-surface rounded-lg border border-border p-6">
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Employé *</label>
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
            className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary mb-1"
          />
          <select
            value={idSelectionne}
            onChange={e => {
              const val = e.target.value;
              setValue('idEmploye', val);
              setIdSelectionne(val);
              const emp = employes.find((emp: any) => emp.idEmploye === val);
              if (emp) setRecherche(`${emp.nom} ${emp.prenoms}`);
            }}
            className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={chargementEmployes}
          >
            <option value="" disabled>
              {chargementEmployes ? 'Chargement...' : `${employesFiltres.length} employé(s) — sélectionner`}
            </option>
            {/* Toujours inclure l'employé pré-sélectionné même s'il n'est pas dans la page courante */}
            {employePreselectionne && !employesFiltres.find((e: any) => e.idEmploye === employePreselectionne.idEmploye) && (
              <option key={employePreselectionne.idEmploye} value={employePreselectionne.idEmploye}>
                {employePreselectionne.nom} {employePreselectionne.prenoms} — {employePreselectionne.fonction} ({employePreselectionne.salaireBase.toLocaleString('fr-FR')} F)
              </option>
            )}
            {employesFiltres.map((e: any) => (
              <option key={e.idEmploye} value={e.idEmploye}>
                {e.nom} {e.prenoms} — {e.fonction} ({e.salaireBase.toLocaleString('fr-FR')} F)
              </option>
            ))}
          </select>
          {errors.idEmploye && <p className="text-danger text-xs mt-1">{errors.idEmploye.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Période *</label>
          <PeriodeSelector mois={mois} annee={annee} onChange={(m, a) => { setValue('mois', m); setValue('annee', a); }} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[['avanceAcompte', 'Avance / Acompte'], ['oppositions', 'Oppositions'], ['taxesTemporaires', 'Taxes temporaires']].map(([field, label]) => (
            <div key={field}>
              <label className="block text-xs font-medium text-content-muted mb-1">{label}</label>
              <input
                type="number"
                {...register(field as any, { valueAsNumber: true })}
                min={0}
                className="w-full border border-border rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          ))}
        </div>

        {creerBulletin.isError && (
          <p className="text-danger text-sm bg-danger-light p-3 rounded">{(creerBulletin.error as any)?.message ?? 'Erreur lors de la génération'}</p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSubmitting || creerBulletin.isPending}
            className="flex-1 bg-accent text-content-inverted py-2 rounded font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creerBulletin.isPending ? 'Génération...' : 'Générer le bulletin'}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-border rounded text-sm hover:bg-surface-raised">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}
